/**
 * Copyright 2021-2022 Highway9 Networks Inc.
 */
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";
import { RootState, store } from "..";
import { CONNECTED, DEVICE_VIEW, IDLE, } from "~/constants";
import { metricHelper } from "../../helpers/metricHelper";
import { authenticationService, dnnService, subscriberService } from "../../services";
import { Device } from "../../types/device";
import { DeviceInfo } from "../../types/deviceInfo";
import { DeviceMetrics, DeviceMetricsData, networkMetricsList, allMetricsList } from "../../types/DeviceMetric";
import { defaultDNN, IDNN } from "../../types/dnn";
import Radio from "../../types/radio";
import { calcLengths, groupBy, normalizeTime } from "~/helpers/utils";
import {
  EventGraphData,
  TimeLine,
  TimeLineEvent,
  formatDeviceEventData,
} from "~/views/subscribers/graphs/TimelineGraphHelper";
import { defaultViewHiddenColumns } from "~/views/subscribers/useDevicePage";
import { EventQuery } from "~/types/event"
import { DeviceGroup } from "~/types/deviceGroup";
import { MetricsClient } from "~/sw/metrics/metricClient";
import { MetricsQuery } from "~/services/APIServices";
import { TransformedTimelineData } from "~/sw/fetch/device/timeline";
import { DeviceTimelineClient, DeviceTimelineClientEvents } from "~/sw/events/deviceTimelineClient";
import { radioData } from "./radioSlice";
import { isPartnerKpiOnlyUser } from "~/helpers/partnerKpiOnly";
// Variables to keep track of the last refresh time, previous device ID, and previous interval for the pingmon data
let lastRefreshTime = 0;
let prevDeviceID = "";
let prevInterval = 0;

export const subscriberMetricsClient1 = new MetricsClient();
export const subscriberMetricsClient2 = new MetricsClient(); // For radio-sourced metrics
export const timelineClient = new DeviceTimelineClient();

type initState = {
  open: boolean;
  loading: boolean;
  connectionQualityLoading: boolean;

  importOpen: boolean;
  detailsOpen: boolean;

  networksData: Array<IDNN>;
  currentNetwork: IDNN;
  networkOpen: boolean;

  status: string;
  current: null | Device;
  data: Array<Device>;
  dataDeviceInfo: Array<DeviceInfo>;
  hiddenColumns: Array<string>;
  preset: string;

  timelineGraph: {
    eventShow: "all" | "major" | "error";
    showLogs: boolean;
    loading: boolean;
    timelineData: TimeLine;
    timelineEvents: TimeLineEvent[];
    timestamps: number[][];
    deviceEvents: EventGraphData[];
  };
  metrics: DeviceMetricsData;
  metricsSource: "device" | "radio";
  deviceGroupMappingID: { [key: string]: number };
  connectedDeviceGroups: { [key: string]: number };
  pingmon: {
    pingmonMetrics: {
      ping: any[];
      loss: any[];
      jitter: any[];
      min: number;
      max: number;
      average: number;
      successRate: number;
    };
    raw: any[];
    showLogs: boolean;
  };

  currentMetrics: {
    [key: string]: DeviceMetrics;
  };

  simulation: {
    [deviceId : string]: {
      selectedEdge: string;
      command: string;
      response: string;
      isRunning: boolean;
      operationId: string;
    };
  },

  runningSimulationEdgeIds : string[];
};

const initialState: initState = {
  open: false,
  loading: true,
  connectionQualityLoading: false,

  importOpen: false,
  detailsOpen: false,

  networksData: [],
  currentNetwork: defaultDNN,
  networkOpen: false,
  deviceGroupMappingID: {},
  connectedDeviceGroups: {},

  status: "",
  current: null,
  data: [],
  dataDeviceInfo: [],
  hiddenColumns: defaultViewHiddenColumns,
  preset: DEVICE_VIEW.DEFAULT_VIEW,

  timelineGraph: {
    eventShow: "major",
    showLogs: false,
    loading: true,
    timelineData: {},
    timelineEvents: [],
    timestamps: [],
    deviceEvents: [],
  },
  metrics: {
    device_upload_throughput: [],
    device_download_throughput: [],
    sent_bytes: [],
    received_bytes: [],
  },

  metricsSource: "device",

  pingmon: {
    pingmonMetrics: {
      ping: [],
      loss: [],
      jitter: [],
      min: 0,
      max: 0,
      average: 0,
      successRate: 0,
    },
    raw: [],
    showLogs: false,
  },

  currentMetrics: {},

  simulation: {},
  runningSimulationEdgeIds : [],
};

export const fetchDevices = createAsyncThunk(
  `subscriber/fetchDevices`,
  async (_, thunk) => {
    try {
      const devices = await subscriberService.getSubscribers();

      const store = thunk.getState() as RootState;
      const radios = store.radio.data;
      const deviceGroups = store.deviceGroup.data;

      return {
        radios,
        devices: devices.sort((a, b) => a.name.localeCompare(b.name)),
        deviceGroups
      };
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
  // MCS-3777: backend returns 403 for RAN_KPI_ONLY for /devices (the underlying fetch fires from
  // sw/fetch/device via subscriberService → apiFetch worker). Gates the redux thunk so callers from
  // useDevicePage and the websocket refreshDevice event no longer trigger the worker fetch.
  { condition: () => !isPartnerKpiOnlyUser() },
);


export const fetchTimelineGraph = createAsyncThunk(
  `subscriber/fetchTimelineGraph`,
  async (payload: { id: string; }, thunk) => {
    const state = thunk.getState() as RootState;
    const { startTime, endTime, interval } = state.utility.time;
    const params = {
      objectType: "DEVICE",
      objectId: payload.id,
      interval: { startTime, endTime: interval === 'custom' ? endTime : Math.floor(Date.now() / 1000) },
      queryType: "DEVICE_TIMELINE"
    } as EventQuery;

    return new Promise<TransformedTimelineData>((resolve, reject) => {
      timelineClient.initialize(
        params,
        authenticationService.currentUserValue!.token,
        (_id, _workerID, _data) => {
          resolve(_data as TransformedTimelineData);
        },
        { interval: 30, noPolling: interval === 'custom' }
      );
    });
  }
);

/* The above code is a TypeScript function that defines a thunk action creator `fetchDeviceMetrics`
using the `createAsyncThunk` function from the Redux Toolkit. This thunk action is used to fetch
device metrics data for a specific subscriber ID asynchronously. */
export const fetchDeviceMetrics = createAsyncThunk(`subscriber/fetchDeviceMetrics`, async (id: string, thunk) => {
  const state = thunk.getState() as RootState;
  const { startTime, endTime, interval, timeZone } = state.utility.time;
  const resolution = metricHelper.getResolution(startTime, endTime, 500);
  const params : MetricsQuery = {
    metrics: ["device_upload_throughput", "device_download_throughput", "sent_bytes", "received_bytes"],
    interval: {
      startTime: normalizeTime(startTime, resolution, timeZone),
      endTime: normalizeTime(endTime, resolution, timeZone)
    },
    resolution,
    ids: [id],
  };

  let data: typeof initialState.metrics = {};

  await subscriberMetricsClient1.initialize('devices', params, authenticationService.currentUserValue!.token, (id, metrics) => {
    data = metrics as typeof initialState.metrics;
  }, { noPolling: interval === 'custom' });

  return data;
});


/* Legacy function: Fetch all connection quality metrics with fallback behavior
 * @deprecated Use fetchRadioSourcedMetrics and fetchCPEMetrics separately for better control
 * This fetches metrics with "device|radio" source query which tries device first, then falls back to radio
 * NOTE: Currently disabled - no metrics are fetched by this function
 */
export const fetchConnectionQualityMetrics = createAsyncThunk(
  `subscriber/fetchConnectionQualityMetrics`,
  async (id: string, thunk) => {
    const state = thunk.getState() as RootState;
    const { startTime, endTime, interval, timeZone } = state.utility.time;
    const metricsSource = state.subscriber.metricsSource;
    const resolution = metricHelper.getResolution(startTime, endTime, 500);
    const params : MetricsQuery = {
      metrics: allMetricsList,
      interval: {
        startTime: normalizeTime(startTime, resolution, timeZone),
        endTime: normalizeTime(endTime, resolution, timeZone)
      },
      resolution,
      ids: [id],
      queryKeys: [{ key: "source", value: metricsSource }],
    };
    let data: typeof initialState.metrics = {};
    await subscriberMetricsClient2.initialize('devices', params, authenticationService.currentUserValue!.token, (id, metrics) => {
      data = metrics as typeof initialState.metrics;
    }, { noPolling: interval === 'custom' });

    return data
  }
);


// Function to calculate the new start time
function calculateNewStartTime(id: string, diff: number, startTime: number, endTime: number): number {
  let newStartTime = startTime;
  const syncDiff = endTime - lastRefreshTime;
  // console.table({ syncDiff, lastRefreshTime, endTime, startTime, diff, prevInterval, id, prevDeviceID,  });
  if (syncDiff <= 60 && prevDeviceID === id && prevInterval === diff) {
    newStartTime = lastRefreshTime;
  } else {
    lastRefreshTime = 0;
  }
  return newStartTime;
}

export const fetchPingmon = createAsyncThunk(`subscriber/fetchPingmon`, async (id: string, thunk) => {
  try {
    const state = thunk.getState() as RootState;
    const { startTime, endTime, diff } = state.utility.time;
    const maxAllowedTimeInterval = 24 * 60 * 60; // 1 day in seconds
    const newStartTime = calculateNewStartTime(id, Math.min(diff, maxAllowedTimeInterval), Math.max(startTime, endTime - maxAllowedTimeInterval), endTime);
    const params = {
      metrics: ["pingmon"],
      interval: { startTime: newStartTime, endTime, originalStartTime: startTime },
      resolution: metricHelper.getResolution(startTime, endTime, 300),
      ids: [id],
    };

    const fullSync = lastRefreshTime === 0;
    const raw = state.subscriber.pingmon.raw;



    console.time("pingmon");
    const data = await subscriberService.getPingmonData(params, fullSync, raw);
    const lastPointTimestamp = moment(data.raw[data.raw.length - 1]?.timestamp).unix();
    // console.table({ fullSync, endTime, startTime, newStartTime, diff, newDiff : endTime-newStartTime, lastRefreshTime, prevDeviceID, prevInterval });

    console.timeEnd("pingmon");

    lastRefreshTime = lastPointTimestamp ?? 0;
    prevDeviceID = id;
    prevInterval = diff;

    return data;
  } catch (error) {
    console.log("pingmon err : ", error)
    throw error;
  }
});

export const fetchDevicesLatestMetrics = createAsyncThunk(`subscriber/getLatestMetrics`, async (_: void, thunk) => {
  const state = thunk.getState() as RootState;
  const devices = state.subscriber.data;
  const connectedDeviceIds = devices.filter((d) => d.runtimeInfo?.status === CONNECTED).map((d) => d.id) as string[];
  if (connectedDeviceIds.length === 0) return {};

  return await subscriberService.getLatestMetricSync(connectedDeviceIds);
});

export const fetchDNN = createAsyncThunk("subscriber/fetchDNN", async () => {
  try {
    const dnn = await dnnService.getDnn();
    return dnn;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const fetchSubscriberDeviceInfo = createAsyncThunk("subscriber/fetchSubscriberDeviceInfo", async () => {
  try {
    const deviceInfoData = await subscriberService.getSubscribersDeviceInfo();
    return deviceInfoData;
  } catch (error) {
    console.log(error);
    throw error;
  }
});

export const selectSimulationByDeviceId = (state: RootState, deviceId: string) => {
  return state.subscriber.simulation[deviceId] || {
    selectedEdge: "",
    command: "",
    isRunning: false,
    response: "",
    operationId: "",
  };
};

// Define SimulationState type for simulation state updates
type SimulationState = {
  selectedEdge: string;
  command: string;
  response: string;
  isRunning: boolean;
  operationId: string;
};

const subscriberSlice = createSlice({
  name: "subscriber",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setValues: (state, action: PayloadAction<Device | null>) => {
      state.current = action.payload;
    },
    setCurrentNetworkValues: (state, action: PayloadAction<IDNN>) => {
      state.currentNetwork = action.payload;
    },
    setCurrentNetworkName: (state, action: PayloadAction<string>) => {
      state.currentNetwork.name = action.payload;
    },
    setCurrentNetworkQci: (state, action: PayloadAction<number>) => {
      state.currentNetwork.defaultQci = action.payload;
    },
    setCurrentNetworkAmbr: (state, action: PayloadAction<{ uplink: number; downlink: number }>) => {
      state.currentNetwork.ambr = action.payload;
    },
    setNetworkOpen: (state, action: PayloadAction<boolean>) => {
      state.networkOpen = action.payload;
    },
    setImportOpen: (state, action: PayloadAction<boolean>) => {
      state.importOpen = action.payload;
    },
    setDetailsOpen: (state, action: PayloadAction<boolean>) => {
      state.detailsOpen = action.payload;
    },

    setHiddenColumns: (state, action: PayloadAction<string[]>) => {
      state.hiddenColumns = action.payload;
    },
    setPreset: (state, action: PayloadAction<string>) => {
      state.preset = action.payload;
    },
    setTimelineGraphLoading: (state, action: PayloadAction<boolean>) => {
      state.timelineGraph.loading = action.payload;
    },
     setTimelineEventShow: (state, action) => {
    state.timelineGraph.eventShow = action.payload;
},
    toggleTimelineLogs: (state) => {
      state.timelineGraph.showLogs = !state.timelineGraph.showLogs;
    },

    togglePingmonLogs: (state) => {
      state.pingmon.showLogs = !state.pingmon.showLogs;
    },

    setMetricsSource: (state, action: PayloadAction<"device" | "radio">) => {
      state.metricsSource = action.payload;
    },

    setSimulationState: (
      state,
      action: PayloadAction<{ deviceId: string; updates: Partial<SimulationState> }>
    ) => {
      const { deviceId, updates } = action.payload;
      state.simulation[deviceId] = {
        ...state.simulation[deviceId],
        ...updates,
      };
    },

    resetSimulationState: (state) => {
      state.simulation = {};
    },

    setBusyEdge: (state, action: PayloadAction<{ edgeId: string }>) => {
      if (!state.runningSimulationEdgeIds .includes(action.payload.edgeId)) {
        state.runningSimulationEdgeIds .push(action.payload.edgeId);
      }
    },
    clearBusyEdge: (state, action: PayloadAction<{ edgeId: string }>) => {
      state.runningSimulationEdgeIds  = state.runningSimulationEdgeIds .filter(id => id !== action.payload.edgeId);
    },

  },
  extraReducers: (builder) => {
    builder
      //devices
      .addCase(fetchDevices.fulfilled, (state, action) => {
        const { devices: data, radios, deviceGroups } = action.payload;
        const _devices = SyncDeviceWithRuntimeRadioAndGroups(data, radios, deviceGroups);
        const devices = SyncWithMetrics(_devices, state.currentMetrics);
        state.data = devices ?? [];

        // sync the current subscriber with the new data
        if (state.current && !state.open) {
          const current = state.data.find((device) => device.id === state.current?.id);
          if (current) {
            state.current = current;
          }
        }
        state.loading = false;

        const groupedDevices = groupBy(data, "groupId") as { [key: string]: Device[] };
        // calculate the length of connected devices for each group
        const connectedLengths = Object.keys(groupedDevices).reduce((acc, key) => {
          acc[key] = groupedDevices[key].filter(
            (device) => device.runtimeInfo?.status === CONNECTED || device.runtimeInfo?.status === IDLE
          ).length;
          return acc;
        }, {} as Record<string, number>);

        state.connectedDeviceGroups = connectedLengths;

        const calcLen = calcLengths(groupedDevices);
        state.deviceGroupMappingID = calcLen;
        state.status = "devices_loaded";
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.status = "error_fetching_devices";
      })

      // Network DNN
      .addCase(fetchDNN.fulfilled, (state, action) => {
        state.networksData = action.payload;
        state.loading = false;
        state.status = "dnn_loaded";
      })
      .addCase(fetchDNN.rejected, (state) => {
        state.status = "error_fetching_DNN";
      })

      // Timeline Graph Data
      .addCase(fetchTimelineGraph.pending, (state) => {
        state.timelineGraph.loading = true;
      })
      .addCase(fetchTimelineGraph.fulfilled, (state, action) => {
        const { timelineData, timeEvents, timestamps, events } = action.payload;
        state.timelineGraph.timestamps = timestamps;
        state.timelineGraph.timelineData = timelineData;
        state.timelineGraph.timelineEvents = timeEvents;
        // state.timelineGraph.logs = timelineLogs;
        state.timelineGraph.loading = false;
        state.loading = false;

        state.timelineGraph.deviceEvents = formatDeviceEventData(events);
        state.status = "timeline_graph_loaded";
      })
      .addCase(fetchTimelineGraph.rejected, (state) => {
        state.status = "error_fetching_timeline_graph";
      })

      // Throughput Graph Data
      .addCase(fetchDeviceMetrics.fulfilled, (state, action) => {
        const data = action.payload;
        Object.keys(data).forEach((key) => {
          state.metrics[key] = data[key];
        });
        state.loading = false;
        state.status = "throughput_graph_loaded";
      })
      .addCase(fetchDeviceMetrics.rejected, (state) => {
        // remove the metrics from the state
        networkMetricsList.forEach((metric) => {
          delete state.metrics[metric];
        });
        state.status = `error_fetching_throughput_graph`;
      })

     
      .addCase(fetchConnectionQualityMetrics.pending, (state) => {
        state.connectionQualityLoading = true;
        allMetricsList.forEach((metric) => {
          delete state.metrics[metric];
        });
      })
      .addCase(fetchConnectionQualityMetrics.fulfilled, (state, action) => {
        const data = action.payload;
        allMetricsList.forEach((metric) => {
          delete state.metrics[metric];
        });
        
        // Add new metrics that have valid data
        Object.keys(data).forEach((key) => {
          const metricData = data[key];
          // Filter out metrics where all datapoints are null
          const allNull = metricData.every((point) => point[1] === null);
          if (!allNull) {
            state.metrics[key] = data[key];
          }
        });
        state.loading = false;
        state.connectionQualityLoading = false;
        state.status = "connection_quality_graph_loaded";
      })

      .addCase(fetchConnectionQualityMetrics.rejected, (state, action) => {
        state.connectionQualityLoading = false;
        // remove all metrics from the state
        allMetricsList.forEach((metric) => {
          delete state.metrics[metric];
        });
        state.status = `error_fetching_connection_quality_graph`;
      })

      .addCase(fetchDevicesLatestMetrics.fulfilled, (state, action) => {
        const data = action.payload;
        state.currentMetrics = data ?? {};
        // update the devices with the latest metrics
        const devices = state.data;
        const _devices = SyncWithMetrics(devices, data);
        state.data = _devices;

        // sync the current subscriber with the new data
        if (state.current && !state.open) {
          const current = state.data.find((device) => device.id === state.current?.id);
          if (current) {
            state.current = current;
          }
        }

        state.loading = false;
      })
      .addCase(fetchPingmon.fulfilled, (state, action) => {
        const { data, raw } = action.payload;
        state.pingmon.pingmonMetrics = data;
        state.pingmon.raw = raw;
        state.loading = false;
      })
      .addCase(fetchPingmon.rejected, (state) => {
        state.status = `error_fetching_pingmon`;
      })
      .addCase(fetchSubscriberDeviceInfo.fulfilled, (state, action) => {
        state.dataDeviceInfo = action.payload;
      });
  },
});

export const subscriberActions = subscriberSlice.actions;
export default subscriberSlice.reducer;
export const subscriberOpen = (state: RootState) => state.subscriber.open;
export const subscriberHiddenColumns = (state: RootState) => state.subscriber.hiddenColumns;
export const subscriberPreset = (state: RootState) => state.subscriber.preset;

export const subscriberImportOpen = (state: RootState) => state.subscriber.importOpen;
export const subscriberDetailsOpen = (state: RootState) => state.subscriber.detailsOpen;

export const subscriberNetworksData = (state: RootState) => state.subscriber.networksData;
export const subscriberCurrentNetwork = (state: RootState) => state.subscriber.currentNetwork;
export const subscriberLoading = (state: RootState) => state.subscriber.loading;
export const subscriberConnectionQualityLoading = (state: RootState) => state.subscriber.connectionQualityLoading;
export const subscriberNetworkOpen = (state: RootState) => state.subscriber.networkOpen;

export const subscriberData = (state: RootState) => state.subscriber.data;
export const subscriberSelected = (state: RootState) => state.subscriber.current;
export const subscriberTimelineGraphLoading = (state: RootState) => state.subscriber.timelineGraph.loading;
export const subscriberDeviceInfoData = (state: RootState) => state.subscriber.dataDeviceInfo;

export const subscriberTimelineData = (state: RootState) => state.subscriber.timelineGraph.timelineData;
export const subscriberTimelineEvents = (state: RootState) => state.subscriber.timelineGraph.timelineEvents;

export const deviceConnectedToTechnology = createSelector([subscriberSelected , radioData], (device, radioData) => {
  const radioId = device?.runtimeInfo?.radioId;
  const radio = radioData.find((radio) => radio.id === radioId) ?? null;
  return device?.runtimeInfo?.connectedNetworkType ?? radio?.technology ?? "4G"; // if the device is not connected to a radio, return 4G
});

export const subscriberMetrics = (state: RootState) => state.subscriber.metrics;
export const subscriberMetric = (metric: string) => (state: RootState) => state.subscriber.metrics[metric];
export const pingmonMetrics = (state: RootState) => state.subscriber.pingmon.pingmonMetrics;
export const pingmonRawData = (state: RootState) => state.subscriber.pingmon.raw;

export const subscriberDeviceGroupsMapping = (state: RootState) => state.subscriber.deviceGroupMappingID;
export const subscriberConnectedDeviceGroups = (state: RootState) => state.subscriber.connectedDeviceGroups;

export const subscriberMetricsError = (state: RootState) => state.subscriber.status.startsWith("error");

export const subscriberMetricsSource = (state: RootState) => state.subscriber.metricsSource;
export const subscriberShowDeviceMetrics = (state: RootState) => state.subscriber.metricsSource === "device";
export const subscriberShowNetworkMetrics = (state: RootState) => state.subscriber.metricsSource === "radio";

// Check whether the loaded connection-quality payload has any valid data.
export const subscriberHasMetricsData = (state: RootState) => {
  const metrics = state.subscriber.metrics;
  return allMetricsList.some((metricKey) => {
    const metricData = metrics[metricKey];
    if (!metricData || metricData.length === 0) return false;
    // Check if there's at least one non-null value
    return metricData.some((point) => point[1] !== null);
  });
};

// metrics workers
setTimeout(() => {

  subscriberMetricsClient1.eventEmitter().on('worker:data', async (id, workerID, metrics) => {
    store.dispatch({ type: 'subscriber/fetchDeviceMetrics/fulfilled', payload: metrics });
  });
  subscriberMetricsClient2.eventEmitter().on('worker:data', async (id, workerID, metrics) => {
    store.dispatch({ type: 'subscriber/fetchConnectionQualityMetrics/fulfilled', payload: metrics });
  });

  subscriberMetricsClient1.eventEmitter().on('worker:error', async (id, workerID, error) => {
    store.dispatch({ type: 'subscriber/fetchDeviceMetrics/rejected', payload: error });
  });
  subscriberMetricsClient2.eventEmitter().on('worker:error', async (id, workerID, error) => {
    store.dispatch({ type: 'subscriber/fetchConnectionQualityMetrics/rejected', payload: error });
  });

  DeviceTimelineClientEvents.on('worker:data', async (id, workerID, data) => {
      const _workerID = await timelineClient.getWorkerID();
      if(workerID === _workerID) {
        store.dispatch({
          type: 'subscriber/fetchTimelineGraph/fulfilled',
          payload: data
        })
      }
    })
}, 0)



function SyncDeviceWithRuntimeRadioAndGroups(data: Device[], radioAPs: Radio[], deviceGroups: DeviceGroup[]) {
  return data.map((row) => {
    const deviceGroup = deviceGroups.find((group) => group.id === row?.groupId);
    const _radio = radioAPs.find((radio: any) => radio.id === row.runtimeInfo?.radioId);

    const isDualCarrierAttached = _radio?.carrierMode === "DUAL_CARRIER";
    const attachedDualCarrierCell = _radio?.cellParameters?.find((item) => {
      return item.cellId === row.runtimeInfo?.radioApId;
    })?.cellIndex as string | undefined;

    let radioApName = radioAPs?.find((radioAp) => radioAp.id === row?.runtimeInfo?.radioId)?.name;
    if (!radioApName && row?.runtimeInfo?.radioApId) {
      radioApName = radioAPs?.find((radioAp) =>
        radioAp?.cellParameters?.find((cell) => cell?.cellId === row?.runtimeInfo?.radioApId)
      )?.name;
    }
    if (!radioApName && row?.runtimeInfo?.radioId) {
      radioApName = row?.runtimeInfo?.eci?.toString();
    }

    let runtimeInfo = row.runtimeInfo;
    if (row.runtimeInfo && radioApName) {
      runtimeInfo = {
        ...row.runtimeInfo,
        radioApName,
        radioID: row?.runtimeInfo?.radioId || row?.runtimeInfo?.radioApId,
      };
    }
    if (isDualCarrierAttached && row.runtimeInfo) {
      runtimeInfo = {
        ...(runtimeInfo ?? row.runtimeInfo),
        radioCellNumber: attachedDualCarrierCell,
      };
    }

    return {
      ...row,
      ...(deviceGroup ? { deviceGroupName: deviceGroup.name } : {}),
      ...(runtimeInfo !== row.runtimeInfo ? { runtimeInfo } : {}),
    };
  });
}

function SyncWithMetrics(
  devices: Device[],
  metricsObject: {
    [key: string]: DeviceMetrics;
  }
) {
  return devices.map((device) => {
    const deviceMetrics = metricsObject[device.id!];
    if (deviceMetrics) {
      return { ...device, metrics: deviceMetrics };
    }
    return device;
  });
}
